// ─── Guided Setup: seat occupancy ─────────────────────────────────────────────
//
// detector_config.seat_occupancy, per camera: a polygon per seat, plus the work
// hours the sampling runs in.
//
// Draw the polygon around where the PERSON appears, not around the chair. On an
// office camera people across a table are visible from the chest up, so the shape
// that matters covers head and torso — and membership is decided by the CENTRE of
// the detected person box, the same centroid rule every zone in this system uses.
// Drawn around the seat of the chair, a sitting person's centre lands above it
// and the seat reads empty all day.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import { Panel, SettingRow, Advanced, countChanged, PolygonList, useModuleSaver }
  from './SetupBits';

const DETECTOR = 'seat_occupancy';

// `def` matches IndustrialSeatsPage's DEF, which matches the detector.
const ASK = [
  { key: 'work_start', label: 'Work starts', type: 'time', def: '09:30', ask: true },
  { key: 'work_end',   label: 'Work ends',   type: 'time', def: '18:30', ask: true },
];
const ADVANCED = [
  { key: 'skip_outside_hours', label: 'Stop sampling outside those hours', type: 'bool', def: true,
    hint: 'Leave ON — an empty office overnight is not an occupancy problem, and the pass costs CPU.' },
  { key: 'sample_secs', label: 'Seconds between samples', type: 'int', min: 2, max: 120, step: 1, def: 10 },
  { key: 'confirm_samples', label: 'Samples to confirm a change', type: 'int', min: 1, max: 10, step: 1, def: 2 },
  { key: 'window_samples', label: 'Samples in the window', type: 'int', min: 1, max: 20, step: 1, def: 3 },
  { key: 'merge_gap_secs', label: 'Join two sittings less than (s) apart', type: 'int',
    min: 0, max: 1800, step: 30, def: 180,
    hint: 'Somebody stepping away for a minute is still one sitting, not two.' },
  { key: 'checkpoint_secs', label: 'Write a checkpoint every (s)', type: 'int',
    min: 60, max: 1800, step: 60, def: 300 },
  { key: 'conf',  label: 'Confidence', type: 'range', min: 0.1, max: 0.9, step: 0.05, def: 0.35 },
  { key: 'imgsz', label: 'Image size', type: 'int', min: 640, max: 1920, step: 320, def: 640 },
];

const DEF = () => ({
  seats: [],
  ...Object.fromEntries([...ASK, ...ADVANCED].map(s => [s.key, s.def])),
});

export default function SeatsPanel({ cameras, onChanged }) {
  const [cams, setCams] = useState(null);
  const [edit, setEdit] = useState(null);
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/cameras')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCams(d.cameras || []); })
    .catch(() => setErr('Could not read the cameras.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  const rows = (cams || [])
    .filter(c => cameras.some(x => x.idx === c.idx))
    .map(c => ({
      idx: c.idx,
      name: c.name || `Camera ${c.idx}`,
      on: (c.detectors || []).includes(DETECTOR),
      cfg: (c.detector_config || {})[DETECTOR] || null,
    }));

  async function save() {
    const cam  = (cams || []).find(c => c.idx === edit.idx);
    const dets = [...new Set([...(cam?.detectors || []), DETECTOR])];
    const { idx, ...payload } = edit;
    const d = await run('PUT', `/cameras/${idx}`,
                        { detectors: dets, detector_config: { [DETECTOR]: payload } },
                        'the seats');
    if (d) { setEdit(null); await load(); }
  }

  async function remove(idx) {
    if (!window.confirm('Stop watching seats on this camera? Records already collected are kept.')) return;
    const cam  = (cams || []).find(c => c.idx === idx);
    const dets = (cam?.detectors || []).filter(d => d !== DETECTOR);
    if (await run('PUT', `/cameras/${idx}`, { detectors: dets }, 'the change')) await load();
  }

  // ── editing one camera ──
  if (edit) {
    const cam = rows.find(r => r.idx === edit.idx);
    return (
      <>
        <div className="gs-section-title">Seats on {cam?.name}</div>

        <Panel title="Draw around the person, not the chair">
          A seat counts as taken when the MIDDLE of a detected person is inside the shape.
          On a camera looking across a table that means head and chest — drawn tight around
          the chair, a seated person’s middle lands above it and the seat reads empty all day.
        </Panel>

        <PolygonList cameraIdx={edit.idx} value={edit.seats} noun="seat" named
                     onChange={s => setEdit(e => ({ ...e, seats: s }))} />

        <div className="gs-section-title">When is it watching?</div>
        {ASK.map(s => (
          <SettingRow key={s.key} spec={s} value={edit[s.key]}
                      onChange={v => setEdit(e => ({ ...e, [s.key]: v }))} />
        ))}

        <Advanced changed={countChanged(ADVANCED, edit)}>
          {ADVANCED.map(s => (
            <SettingRow key={s.key} spec={s} value={edit[s.key]}
                        onChange={v => setEdit(e => ({ ...e, [s.key]: v }))} />
          ))}
        </Advanced>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="gs-btn primary" onClick={save} disabled={busy || !edit.seats.length}>
            {busy ? <span className="gs-spin" /> : 'Save seats'}
          </button>
          <button className="gs-btn ghost" onClick={() => { setEdit(null); setErr(null); }}>Cancel</button>
          {!edit.seats.length && <span className="gs-foot-note">Add at least one seat</span>}
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── the list ──
  return (
    <>
      {!cams && !err && (
        <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>
      )}

      {cams && (
        <>
          <p className="gs-help">
            Mark each seat on a camera and it reports when it was occupied and for how
            long. Nobody is identified — this counts chairs, not people.
          </p>

          {rows.map(r => {
            const seats = r.cfg?.seats || [];
            return (
              <div className={`gs-row${r.on && !seats.length ? ' gs-row-bad' : ''}`} key={r.idx}>
                <span className="gs-row-icon">{!r.on ? '📷' : seats.length ? '💺' : '⚠️'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{r.name}</span>
                  <span className="gs-row-sub">
                    {!r.on ? 'No seats marked'
                      : seats.length === 0 ? 'Switched on with no seats — it reports nothing'
                      : `${seats.length} seat${seats.length === 1 ? '' : 's'} · ${r.cfg.work_start || '09:30'}–${r.cfg.work_end || '18:30'}`}
                  </span>
                </span>
                <button className="gs-btn small"
                        onClick={() => { setErr(null);
                          setEdit({ ...DEF(), ...(r.cfg || {}),
                                    seats: (r.cfg?.seats || []).map(s => ({ ...s })),
                                    idx: r.idx }); }}>
                  {r.on ? 'Change' : 'Mark seats'}
                </button>
                {r.on && (
                  <button className="gs-btn small ghost" disabled={busy}
                          onClick={() => remove(r.idx)}>Stop</button>
                )}
              </div>
            );
          })}

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
